import { useState, useRef } from 'react';
import { Image as ImageIcon, Trash, PaperPlaneTilt } from '@phosphor-icons/react';
import { useAssetStore } from '../../stores/assetStore';
import { useAuthStore } from '../../stores/authStore';
import type { Asset } from '@spine-workspace/shared';

interface DockedAssetRowProps {
  asset: Asset;
  isSelected: boolean;
  isMultiSelected?: boolean;
  onClick: (asset: Asset, e: React.MouseEvent) => void;
  onDoubleClick?: (asset: Asset) => void;
  onDragStart?: (e: React.DragEvent, asset: Asset) => void;
}

export default function DockedAssetRow({
  asset,
  isSelected,
  isMultiSelected,
  onClick,
  onDoubleClick,
  onDragStart,
}: DockedAssetRowProps) {
  const { deleteAsset, renameAsset, submitAsset } = useAssetStore();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const isImage = asset.spineVersion === 'image';
  const submissionStatus = asset.submissionStatus ?? 'none';
  const canSubmit = asset.ownerId === currentUserId && submissionStatus === 'none' && !isImage;

  const highlighted = isSelected || isMultiSelected;

  function commitRename() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== asset.name) renameAsset(asset.id, trimmed);
    setRenaming(false);
  }

  return (
    <div
      data-testid={`asset-row-${asset.id}`}
      draggable
      title="Double-click to add at center, or drag onto canvas"
      className={`group flex items-center gap-2 px-2 py-[3px] mx-1 rounded cursor-pointer select-none transition-colors duration-fast ${
        highlighted
          ? 'bg-accent-muted outline outline-[1.5px] outline-accent/30'
          : 'hover:bg-bg-hover'
      }`}
      onClick={(e) => onClick(asset, e)}
      onDoubleClick={() => onDoubleClick?.(asset)}
      onDragStart={(e) => {
        if (onDragStart) {
          onDragStart(e, asset);
        } else {
          e.dataTransfer.setData('application/spine-asset', JSON.stringify([asset]));
          e.dataTransfer.effectAllowed = 'copy';
        }
      }}
    >
      {/* Type badge */}
      <span
        className={`shrink-0 w-[26px] h-[18px] flex items-center justify-center rounded text-[8px] font-mono font-bold ${
          isImage
            ? 'bg-emerald-500/15 text-emerald-400'
            : 'bg-accent/15 text-accent'
        }`}
      >
        {isImage ? 'IMG' : 'SP'}
      </span>

      {/* Name */}
      <div className="flex-1 min-w-0">
        {renaming ? (
          <input
            ref={inputRef}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') setRenaming(false);
              e.stopPropagation();
            }}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
            data-testid={`asset-name-input-${asset.id}`}
            className="w-full bg-bg-input border border-accent rounded px-1 py-0 text-xs font-sans text-text-primary outline-none"
          />
        ) : (
          <span
            data-testid={`asset-name-${asset.id}`}
            className="block font-sans text-xs text-text-primary truncate"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setDraft(asset.name);
              setRenaming(true);
            }}
          >
            {asset.name}
          </span>
        )}
      </div>

      {/* Submission status badge */}
      {!renaming && submissionStatus !== 'none' && (
        <span
          title={submissionStatus === 'submitted' ? 'Pending review' : 'Reviewed'}
          className={`shrink-0 w-1.5 h-1.5 rounded-full ${
            submissionStatus === 'submitted' ? 'bg-amber-400' : 'bg-emerald-400'
          }`}
        />
      )}

      {/* Hover meta */}
      {!renaming && (
        <span className="shrink-0 text-[9px] font-mono text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity">
          {isImage ? <ImageIcon size={9} /> : `${asset.metadata?.animations?.length ?? 0}a`}
        </span>
      )}

      {/* Submit for review */}
      {!renaming && canSubmit && (
        <button
          className="shrink-0 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-accent-muted transition-all duration-fast"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm(`Submit "${asset.name}" for art lead review?`)) submitAsset(asset.id);
          }}
          title="Submit for review"
          data-testid={`asset-submit-${asset.id}`}
        >
          <PaperPlaneTilt size={10} />
        </button>
      )}

      {/* Delete */}
      {!renaming && (
        <button
          className="shrink-0 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-error hover:bg-error-muted transition-all duration-fast"
          onClick={(e) => {
            e.stopPropagation();
            if (confirm(`Delete "${asset.name}"?`)) deleteAsset(asset.id);
          }}
          title="Delete asset"
        >
          <Trash size={10} />
        </button>
      )}
    </div>
  );
}
